import { useState } from "react";
import {
  FileSignature, Plus, Loader2, X, Trash2, FileDown, ArrowRightCircle,
} from "lucide-react";
import {
  usePresupuestos, useCreatePresupuesto, useSetPresupuestoEstado,
  useConvertPresupuesto, useDeletePresupuesto, openPresupuestoPdf,
  type Presupuesto, type PresupuestoEstado,
} from "../api/presupuestos";
import { useIssuers } from "../api/issuers";
import { useClientes } from "../api/clientes";

const eur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

const ESTADO_BADGE: Record<PresupuestoEstado, string> = {
  borrador: "bg-slate-100 text-slate-600",
  enviado: "bg-sky-50 text-sky-700",
  aceptado: "bg-emerald-50 text-emerald-700",
  rechazado: "bg-rose-50 text-rose-600",
  facturado: "bg-violet-50 text-violet-700",
};

export function PresupuestosPage() {
  const [estado, setEstado] = useState("");
  const { data: presupuestos, isLoading } = usePresupuestos(estado);
  const setEstadoMut = useSetPresupuestoEstado();
  const convertir = useConvertPresupuesto();
  const eliminar = useDeletePresupuesto();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0 overflow-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <FileSignature size={26} className="text-brand-700" /> Presupuestos
          </h1>
          <p className="text-slate-500 mt-1">
            Ofertas para clientes. Al aceptarse, conviértelas en factura con un clic.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 text-sm font-bold"
        >
          <Plus size={16} /> Nuevo presupuesto
        </button>
      </header>

      <select
        className="w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm"
        value={estado}
        onChange={(e) => setEstado(e.target.value)}
      >
        <option value="">Todos los estados</option>
        {(["borrador", "enviado", "aceptado", "rechazado", "facturado"] as const).map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 bg-white overflow-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="text-left text-xs font-black uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">Nº</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Emisor</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(presupuestos ?? []).map((p) => (
                <PresupuestoRow
                  key={p.id}
                  p={p}
                  onEstado={(e) => setEstadoMut.mutate({ id: p.id, estado: e })}
                  onConvertir={() => {
                    if (confirm(`Se emitirá una factura real a partir de ${p.numero}. ¿Continuar?`)) {
                      convertir.mutate(p.id, {
                        onSuccess: (r) => alert(`Factura ${r.factura_numero} emitida.`),
                        onError: () => alert("No se pudo convertir."),
                      });
                    }
                  }}
                  onEliminar={() => {
                    if (confirm("¿Eliminar este presupuesto?")) eliminar.mutate(p.id);
                  }}
                />
              ))}
              {!presupuestos?.length && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Sin presupuestos. Crea el primero.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function PresupuestoRow({
  p, onEstado, onConvertir, onEliminar,
}: {
  p: Presupuesto;
  onEstado: (e: PresupuestoEstado) => void;
  onConvertir: () => void;
  onEliminar: () => void;
}) {
  const facturado = p.estado === "facturado";
  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50/60">
      <td className="px-4 py-3 font-mono font-bold text-slate-900">{p.numero}</td>
      <td className="px-4 py-3 text-slate-600">{new Date(p.fecha).toLocaleDateString("es-ES")}</td>
      <td className="px-4 py-3 text-slate-600">{p.emisor?.nombre ?? "—"}</td>
      <td className="px-4 py-3 font-semibold text-slate-800">{p.cliente?.nombre ?? "—"}</td>
      <td className="px-4 py-3 text-right font-black text-slate-900">{eur(Number(p.total))}</td>
      <td className="px-4 py-3">
        {facturado ? (
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ESTADO_BADGE[p.estado]}`}>facturado</span>
        ) : (
          <select
            className={`rounded-full px-2 py-1 text-xs font-bold border-0 ${ESTADO_BADGE[p.estado]}`}
            value={p.estado}
            onChange={(e) => onEstado(e.target.value as PresupuestoEstado)}
          >
            {(["borrador", "enviado", "aceptado", "rechazado"] as const).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1.5">
          <button title="PDF" onClick={() => void openPresupuestoPdf(p.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><FileDown size={16} /></button>
          {!facturado && (
            <button title="Convertir en factura" onClick={onConvertir} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"><ArrowRightCircle size={16} /></button>
          )}
          {!facturado && (
            <button title="Eliminar" onClick={onEliminar} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ── Modal de creación ─────────────────────────────────── */

interface LineaForm {
  concepto: string;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  iva_porcentaje: number;
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const create = useCreatePresupuesto();
  const { data: emisores } = useIssuers();
  const [emisorId, setEmisorId] = useState("");
  const { data: clientes } = useClientes(emisorId ? Number(emisorId) : undefined);
  const [clienteId, setClienteId] = useState("");
  const [validez, setValidez] = useState("");
  const [notas, setNotas] = useState("");
  const [lineas, setLineas] = useState<LineaForm[]>([
    { concepto: "", descripcion: "", cantidad: 1, precio_unitario: 0, iva_porcentaje: 21 },
  ]);

  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
  const total = lineas.reduce((acc, l) => {
    const base = l.cantidad * l.precio_unitario;
    return acc + base + (base * l.iva_porcentaje) / 100;
  }, 0);

  const setLinea = (i: number, patch: Partial<LineaForm>) =>
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId || !clienteId || !lineas.some((l) => l.descripcion.trim())) return;
    await create.mutateAsync({
      emisor_id: Number(emisorId),
      cliente_id: Number(clienteId),
      validez_hasta: validez || null,
      notas: notas || null,
      lineas: lineas
        .filter((l) => l.descripcion.trim())
        .map((l) => ({ ...l, concepto: l.concepto || null })),
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm overflow-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4 my-8">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-slate-900">Nuevo presupuesto</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Emisor *</span>
            <select className={input} value={emisorId} onChange={(e) => { setEmisorId(e.target.value); setClienteId(""); }}>
              <option value="">Selecciona…</option>
              {emisores?.map((em) => <option key={em.id} value={em.id}>{em.nombre}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Cliente *</span>
            <select className={input} value={clienteId} onChange={(e) => setClienteId(e.target.value)} disabled={!emisorId}>
              <option value="">Selecciona…</option>
              {clientes?.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Validez hasta</span>
            <input type="date" className={input} value={validez} onChange={(e) => setValidez(e.target.value)} />
          </label>
        </div>

        {/* Líneas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 uppercase">Líneas</span>
            <button type="button" onClick={() => setLineas((ls) => [...ls, { concepto: "", descripcion: "", cantidad: 1, precio_unitario: 0, iva_porcentaje: 21 }])} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">
              <Plus size={13} /> Añadir línea
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_90px_70px_32px] gap-2 items-center">
                <input className={input} placeholder="Descripción *" value={l.descripcion} onChange={(e) => setLinea(i, { descripcion: e.target.value })} />
                <input className={input} type="number" step="0.01" title="Cantidad" value={l.cantidad} onChange={(e) => setLinea(i, { cantidad: Number(e.target.value) })} />
                <input className={input} type="number" step="0.01" title="Precio" value={l.precio_unitario} onChange={(e) => setLinea(i, { precio_unitario: Number(e.target.value) })} />
                <input className={input} type="number" step="0.01" title="IVA %" value={l.iva_porcentaje} onChange={(e) => setLinea(i, { iva_porcentaje: Number(e.target.value) })} />
                <button type="button" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))} className="p-1.5 rounded text-rose-400 hover:bg-rose-50" disabled={lineas.length === 1}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase">Notas</span>
          <textarea className={`${input} min-h-[60px]`} value={notas} onChange={(e) => setNotas(e.target.value)} />
        </label>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <span className="text-lg font-black text-slate-900">Total: {eur(total)}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={create.isPending || !emisorId || !clienteId} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
              {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
